import { useState } from 'react'
import ScrollToTopButton from '@components/common/ScrollToTopButton'
import { PageHero, Reveal, Icon, ICONS, CtaBand, NAVY, GOLD_ICON, GOLD } from '@components/marketing'

const GUIDES = {
  buyer: {
    label: 'Acheteurs',
    icon: 'user',
    title: 'Guide de l\'acheteur',
    intro: 'Trouvez le bon produit, échangez avec le vendeur et achetez en toute sécurité.',
    steps: [
      { title: 'Créez votre compte', text: 'Inscrivez-vous gratuitement en quelques clics. Vous aurez besoin de :', items: ['Votre nom complet', 'Un numéro de téléphone valide', 'Une adresse email', 'Un mot de passe sécurisé'] },
      { title: 'Recherchez des produits', text: 'Utilisez la barre de recherche ou parcourez les catégories :', items: ['Filtrez par catégorie (Auto-Moto, Immobilier, Informatique…)', 'Recherchez par mot-clé ou par image', 'Découvrez les deals du moment et les vidéos'] },
      { title: 'Consultez les annonces', text: 'Prenez le temps d\'examiner chaque annonce :', items: ['Lisez attentivement la description', 'Regardez toutes les photos et vidéos', 'Vérifiez le prix et les conditions de livraison', 'Consultez le profil du vendeur'] },
      { title: 'Contactez le vendeur', text: 'Communiquez directement via la messagerie KUDHI :', items: ['Posez toutes vos questions sur le produit', 'Négociez le prix si possible', 'Convenez d\'un lieu et d\'une heure de rencontre', 'Demandez des photos supplémentaires si besoin'] },
      { title: 'Finalisez votre achat', text: 'Sécurisez votre transaction :', items: ['Rencontrez le vendeur dans un lieu public', 'Inspectez et testez le produit avant de payer', 'Ne payez jamais à l\'avance sans garantie'] },
    ],
    tips: {
      tone: 'warning',
      title: 'Conseils de sécurité',
      items: ['Méfiez-vous des prix trop bas ou des offres trop alléchantes', 'Ne communiquez jamais vos informations bancaires par message', 'Privilégiez les rencontres en personne dans des lieux publics', 'Signalez toute annonce ou utilisateur suspect'],
    },
  },
  seller: {
    label: 'Vendeurs',
    icon: 'briefcase',
    title: 'Guide du vendeur',
    intro: 'Publiez des annonces qui attirent l\'attention et transformez les contacts en ventes.',
    steps: [
      { title: 'Préparez votre annonce', text: 'Avant de publier, assurez-vous d\'avoir :', items: ['Des photos de qualité du produit (au moins 3)', 'Une description détaillée et honnête', 'Le prix exact ou négociable', 'Votre localisation'] },
      { title: 'Créez votre annonce', text: 'Cliquez sur le bouton « + » et suivez les étapes :', items: ['Choisissez le format : Images, Vidéo ou Bannière', 'Sélectionnez la catégorie appropriée', 'Rédigez un titre clair et une description complète', 'Ajoutez vos meilleures photos ou votre vidéo'] },
      { title: 'Choisissez la durée et payez', text: 'Votre annonce est publiée dès le paiement confirmé :', items: ['Choisissez une durée de 1 jour à 1 mois', 'Payez avec Wave depuis votre téléphone', 'Pendant la période d\'essai, la publication est gratuite'] },
      { title: 'Gérez vos messages', text: 'Soyez réactif avec les acheteurs potentiels :', items: ['Répondez rapidement aux questions', 'Restez courtois et professionnel', 'Soyez flexible sur les horaires de rencontre', 'Acceptez les négociations raisonnables'] },
      { title: 'Finalisez la vente', text: 'Pour une transaction réussie :', items: ['Fixez un rendez-vous dans un lieu sûr', 'Apportez le produit en bon état', 'Vérifiez le paiement avant de remettre l\'article', 'Archivez l\'annonce une fois vendue'] },
    ],
    tips: {
      tone: 'success',
      title: 'Conseils pour réussir',
      items: ['Prenez des photos lumineuses et nettes, sous plusieurs angles', 'Utilisez des mots-clés pertinents dans le titre', 'Répondez aux messages dans les 24 heures', 'Proposez des prix compétitifs et soyez honnête sur l\'état'],
    },
  },
}

const TIP_STYLES = {
  warning: { bg: '#FEF2F2', border: '#FECACA', icon: '#DC2626', iconPath: ICONS.warning, text: '#7F1D1D' },
  success: { bg: '#ECFDF5', border: '#A7F3D0', icon: '#059669', iconPath: ICONS.sparkles, text: '#064E3B' },
}

function GuidePage() {
  const [activeTab, setActiveTab] = useState('buyer')
  const guide = GUIDES[activeTab]
  const tip = TIP_STYLES[guide.tips.tone]

  return (
    <div className="home-font bg-white text-slate-900 overflow-x-hidden">
      <PageHero
        badge="Particuliers et entreprises"
        badgeIcon="doc"
        title="Mode d'emploi"
        highlight="de KUDHI."
        subtitle="Tout ce qu'il faut savoir pour acheter et vendre sur KUDHI, étape par étape."
      >
        <div className="mt-10 inline-flex rounded-md border border-slate-200 bg-white p-1.5 shadow-sm">
          {Object.entries(GUIDES).map(([key, g]) => {
            const active = key === activeTab
            return (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`flex items-center gap-2 rounded-md px-6 sm:px-8 py-3 text-sm font-semibold transition-all ${active ? 'shadow-md' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'}`}
                style={active ? { background: NAVY, color: '#fff' } : {}}
              >
                <Icon d={ICONS[g.icon]} className="w-5 h-5" />
                Pour les {g.label}
              </button>
            )
          })}
        </div>
      </PageHero>

      <section className="pb-24 pt-6">
        <div key={activeTab} className="w-[90%] md:w-[80%] mx-auto grid lg:grid-cols-[1fr_1.7fr] gap-10 lg:gap-16">
          {/* Intro + conseils */}
          <div className="lg:sticky lg:top-28 self-start animate-fade-in-up">
            <p className="text-sm font-bold uppercase tracking-widest" style={{ color: '#B8860B' }}>{guide.steps.length} étapes</p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ color: NAVY }}>{guide.title}</h2>
            <p className="mt-4 text-lg text-slate-600">{guide.intro}</p>

            <div className="mt-8 rounded-md border p-6" style={{ background: tip.bg, borderColor: tip.border }}>
              <h3 className="flex items-center gap-2 font-bold" style={{ color: tip.text }}>
                <span style={{ color: tip.icon }}><Icon d={tip.iconPath} className="w-5 h-5" /></span>
                {guide.tips.title}
              </h3>
              <ul className="mt-4 space-y-2.5">
                {guide.tips.items.map((t) => (
                  <li key={t} className="flex items-start gap-2.5 text-sm leading-relaxed" style={{ color: tip.text }}>
                    <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full" style={{ background: tip.icon }} />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Étapes */}
          <ol className="relative space-y-5">
            <div className="absolute left-[27px] top-6 bottom-6 w-0.5 bg-gradient-to-b from-amber-300 via-amber-200 to-transparent hidden sm:block" />
            {guide.steps.map((s, i) => (
              <Reveal as="li" key={s.title} delay={i * 70} className="relative flex gap-5">
                <span
                  className="relative z-10 flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-md text-xl font-extrabold shadow-md ring-4 ring-white"
                  style={i === 0 ? { background: NAVY, color: GOLD } : { background: GOLD, color: NAVY }}
                >
                  {i + 1}
                </span>
                <div className="flex-1 rounded-md border border-slate-200/70 bg-white p-6 sm:p-7 transition-shadow hover:shadow-lg">
                  <h3 className="text-xl font-bold" style={{ color: NAVY }}>{s.title}</h3>
                  <p className="mt-2 text-slate-600">{s.text}</p>
                  <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                    {s.items.map((item) => (
                      <li key={item} className="flex items-start gap-2.5 text-sm text-slate-700">
                        <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full" style={GOLD_ICON}>
                          <Icon d={ICONS.check} className="w-3 h-3" strokeWidth={3} />
                        </span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      <CtaBand
        badge="À vous de jouer"
        title="Prêt à publier votre première annonce ?"
        text="Créez votre compte gratuitement et profitez de la période d'essai pour publier sans payer."
      />

      <ScrollToTopButton />
    </div>
  )
}

export default GuidePage
